import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { US_STATES, CODE_TO_NAME } from "@/lib/states";
import { RankBadge, RANK_TIERS, getRank } from "@/lib/ranks";
import { PlatformIcons } from "@/lib/platforms";
import { useAuth } from "@/context/AuthContext";
import { detectUsState } from "@/lib/geo";

export default function Leaderboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("players");
    const [state, setState] = useState(null);
    const [lastSpecificState, setLastSpecificState] = useState(null);
    const [countyGame, setCountyGame] = useState("ALL");
    const [countyState, setCountyState] = useState(null);
    const [countyMeta, setCountyMeta] = useState({ state_name: "", total: 0, claimed: 0 });
    const [tierFilter, setTierFilter] = useState("ALL");
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [counties, setCounties] = useState([]);

    // Counties map defaults to the viewer's home state (account state, else IP geo)
    useEffect(() => {
        if (user?.state) { setCountyState(user.state); return; }
        let alive = true;
        detectUsState().then(c => { if (alive) setCountyState(prev => prev || c || "KY"); });
        return () => { alive = false; };
    }, [user]);

    // Players/teams ladder also defaults to the viewer's home state (fallback: National)
    useEffect(() => {
        if (user?.state) { setState(user.state); setLastSpecificState(user.state); return; }
        let alive = true;
        detectUsState().then(c => {
            if (!alive) return;
            setState(prev => prev || c || "ALL");
            if (c) setLastSpecificState(prev => prev || c);
        });
        return () => { alive = false; };
    }, [user]);

    const pickState = (code) => {
        setState(code);
        if (code !== "ALL") setLastSpecificState(code);
    };

    useEffect(() => {
        const params = { limit: 500 };
        if (state !== "ALL") params.state = state;
        api.get("/leaderboard/players", { params }).then(r => setPlayers(r.data));
        api.get("/leaderboard/teams", { params }).then(r => setTeams(r.data));
    }, [state]);

    useEffect(() => {
        if (!countyState) return;
        const params = { state: countyState };
        if (countyGame !== "ALL") params.game = countyGame;
        api.get("/leaderboard/counties", { params })
            .then(r => { setCounties(r.data.rows || []); setCountyMeta({ state_name: r.data.state_name, total: r.data.total, claimed: r.data.claimed }); })
            .catch(() => {});
    }, [countyGame, countyState]);

    return (
        <div className="pt-[68px]" data-testid="leaderboard-page">
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-12">
                <div className="overline mb-6">[ Rankings ] {state === "ALL" ? "National" : `${CODE_TO_NAME[state] || state} state`}</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-8xl">
                    The <span className="text-outline-strong">ladder.</span>
                </h1>
                <p className="mt-8 text-secondary max-w-lg">
                    Live rankings recalculated after every finished match. No decay. No mercy resets.
                </p>
            </section>

            {/* RANK SYSTEM STRIP */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12" data-testid="rank-system-strip">
                <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                    <div className="overline">[ Rank System · Bronze → Champion ]</div>
                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/45">
                        Climb the ladder — every ELO point earned is permanent
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="rank-system-grid">
                    {RANK_TIERS.map((tier, i) => {
                        const next = RANK_TIERS[i + 1];
                        const range = next ? `${tier.min.toLocaleString()} – ${(next.min - 1).toLocaleString()}` : `${tier.min.toLocaleString()}+`;
                        return (
                            <div
                                key={tier.key}
                                data-testid={`rank-tier-card-${tier.key.toLowerCase()}`}
                                className="border clip-frame-sm p-4 bg-[#0A0A0A] card-lift transition-colors relative overflow-hidden"
                                style={{ borderColor: tier.color + "55" }}
                            >
                                <div
                                    className="absolute inset-x-0 top-0 h-[3px]"
                                    style={{ background: tier.color, boxShadow: `0 0 12px ${tier.glow}` }}
                                />
                                <div className="flex items-center justify-between mb-3">
                                    <div
                                        className="w-9 h-9 flex items-center justify-center font-display font-black text-base"
                                        style={{ background: tier.color, color: "#0A0A0A", boxShadow: `0 0 14px ${tier.glow}` }}
                                    >
                                        {tier.label.charAt(0)}
                                    </div>
                                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-white/40">
                                        TIER {i + 1}
                                    </div>
                                </div>
                                <div className="font-display font-black text-xl leading-tight" style={{ color: tier.color }}>
                                    {tier.label}
                                </div>
                                <div className="mt-3 pt-3 border-t border-white/5">
                                    <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-white/40 mb-1">ELO Range</div>
                                    <div className="font-mono text-sm text-white/85 tracking-tight">
                                        {range}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 md:px-10">
                {/* Tabs + state filter */}
                <div className="flex flex-col md:flex-row md:items-center gap-6 border-b border-white/10 pb-4 mb-8">
                    <div className="flex items-center gap-8">
                        {["players", "teams", "counties"].map(t => (
                            <button key={t} onClick={() => setTab(t)}
                                data-testid={`leaderboard-tab-${t}`}
                                className={`font-mono text-xs tracking-[0.24em] uppercase transition-colors ${tab === t ? "text-volt" : "text-white/50 hover:text-white"}`}>
                                {t}
                            </button>
                        ))}
                    </div>
                    {tab !== "counties" && (
                    <div className="md:ml-auto flex items-center gap-3">
                        <span className="overline text-white/40">Scope</span>
                        <div className="flex items-center gap-1 border border-white/10 clip-frame-sm px-1 py-1 bg-[#0A0A0A]">
                            <StatePill code={lastSpecificState} activeCode={state === "ALL" ? "" : state} onClick={() => pickState(lastSpecificState)} label={`State${state !== "ALL" ? ` · ${state}` : ""}`} testId="state-pill-state" />
                            <StatePill code="ALL" activeCode={state} onClick={() => pickState("ALL")} label="National" testId="state-pill-all" />
                        </div>
                        <select value={state} onChange={e => pickState(e.target.value)}
                            data-testid="leaderboard-state-select"
                            className="bg-[#0A0A0A] border border-white/10 focus:border-volt outline-none px-3 py-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white">
                            <option value="ALL">All states</option>
                            {US_STATES.map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                            ))}
                        </select>
                    </div>
                    )}
                    {tab === "counties" && (
                        <div className="md:ml-auto flex flex-wrap items-center gap-4">
                            <select value={countyState || ""} onChange={e => setCountyState(e.target.value)}
                                data-testid="counties-state-select"
                                className="bg-[#0A0A0A] border border-white/10 focus:border-volt outline-none px-3 py-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white">
                                {US_STATES.map(([code, name]) => (
                                    <option key={code} value={code}>{name}</option>
                                ))}
                            </select>
                            <div className="flex items-center gap-1 border border-white/10 clip-frame-sm px-1 py-1 bg-[#0A0A0A]" data-testid="counties-game-filter">
                                {[
                                    ["ALL", "All"],
                                    ["CALL OF DUTY: WARZONE", "Warzone"],
                                    ["CALL OF DUTY: MW4 MULTIPLAYER", "MW4 MP"],
                                ].map(([val, label]) => (
                                    <button key={val} onClick={() => setCountyGame(val)}
                                        data-testid={`counties-filter-${label.toLowerCase().replace(/\s/g, '-')}`}
                                        className={`font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 transition-colors ${countyGame === val ? "bg-volt text-white" : "text-white/50 hover:text-white"}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/50 whitespace-nowrap" data-testid="counties-claimed-label">
                                {countyMeta.state_name || "—"} · <span className="text-volt">{countyMeta.claimed}</span>/{countyMeta.total} claimed
                            </div>
                        </div>
                    )}
                </div>

                {/* Tier filter pills — hidden on counties tab */}
                {tab !== "counties" && (
                    <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="tier-filter-pills">
                        <span className="overline text-white/40 mr-2">Rank</span>
                        <TierPill k="ALL" activeKey={tierFilter} setKey={setTierFilter} label="All" color="#ffffff" />
                        {RANK_TIERS.map(t => (
                            <TierPill key={t.key} k={t.key} activeKey={tierFilter} setKey={setTierFilter} label={t.label} color={t.color} />
                        ))}
                    </div>
                )}

                <div className="space-y-2" data-testid="leaderboard-list">
                    {(() => {
                        if (tab === "counties") return null;
                        const rawRows = tab === "players" ? players : teams;
                        const filtered = tierFilter === "ALL"
                            ? rawRows.slice(0, 50)
                            : rawRows.filter(r => getRank(r.elo).key === tierFilter);
                        if (filtered.length === 0) {
                            return (
                                <div className="border border-white/10 py-16 text-center text-white/40 font-mono text-[11px] tracking-[0.28em] uppercase" data-testid="tier-empty-state">
                                    No {tab} in this tier yet
                                </div>
                            );
                        }
                        return filtered.map((row, i) => {
                        const RowWrap = tab === "players"
                            ? ({ children, ...rest }) => <Link to={`/player/${encodeURIComponent(row.gamer_tag)}`} data-testid={`leaderboard-player-link-${row.gamer_tag}`} {...rest}>{children}</Link>
                            : ({ children, ...rest }) => <div {...rest}>{children}</div>;
                        return (
                        <motion.div key={(row.gamer_tag || row.tag) + "-" + i}
                            initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: Math.min(i * 0.02, 0.4), ease: [0.16, 1, 0.3, 1] }}>
                        <RowWrap className={`grid grid-cols-12 items-center gap-4 border border-white/10 clip-frame-sm px-6 py-4 bg-[#0A0A0A] card-lift ${i < 3 ? "bg-gradient-to-r from-[#0A0A0A] to-[#150A22]" : ""} ${tab === "players" ? "hover:border-volt/40 transition-colors block" : ""}`}>
                            <div className={`col-span-1 font-display font-black text-2xl ${i === 0 ? "text-volt" : i < 3 ? "text-white" : "text-white/40"}`}>
                                #{String(i + 1).padStart(2, "0")}
                            </div>
                            <div className="col-span-6 md:col-span-5">
                                <div className="flex items-center gap-3">
                                    {tab === "teams" && (
                                        <div className="w-9 h-9 border border-white/10 bg-[#050505] flex items-center justify-center overflow-hidden flex-shrink-0" data-testid={`lb-team-logo-${row.tag}`}>
                                            {row.logo_url ? (
                                                <img src={row.logo_url} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="font-display font-black text-volt/50 text-xs">{(row.tag || "").slice(0,2)}</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-display font-extrabold text-lg leading-tight truncate flex items-center gap-2">
                                            <span>{tab === "players" ? `/${row.gamer_tag}` : row.name}</span>
                                            {tab === "players" && <PlatformIcons user={row} size="xs" testId={`lb-platforms-${row.gamer_tag}`} />}
                                        </div>
                                        <div className="text-secondary text-xs mt-1">
                                            {tab === "players" ? `${row.name} · ${row.state || "—"}` : `${row.tag} · ${row.home_county || row.home_state || "—"}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-span-3 font-mono text-xs text-white/60 uppercase">
                                {tab === "players" ? (row.main_game || "MULTI") : row.game}
                            </div>
                            <div className="col-span-2 hidden md:block font-mono text-xs text-white/60">
                                {row.wins}W · {row.losses}L
                            </div>
                            <div className="col-span-2 md:col-span-1 text-right">
                                <div className="font-display font-black text-xl text-volt">{row.elo}</div>
                                <div className="overline text-white/40">ELO</div>
                                <div className="mt-1.5 flex md:justify-end">
                                    <RankBadge elo={row.elo} size="xs" testId={`lb-rank-${row.gamer_tag || row.tag}`} />
                                </div>
                            </div>
                        </RowWrap>
                        </motion.div>
                    );});
                    })()}

                    {tab === "counties" && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="counties-grid">
                            {counties.map((c, i) => (
                                <motion.div key={c.county}
                                    initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                                    transition={{ duration: 0.35, delay: Math.min(i * 0.005, 0.3), ease: [0.16, 1, 0.3, 1] }}
                                    className={`relative border clip-frame-sm p-4 min-h-[128px] transition-colors ${c.top_squad ? "border-white/10 bg-[#0A0A0A] card-lift" : "border-white/5 bg-[#050505]"}`}
                                    data-testid={`county-card-${c.county.toLowerCase()}`}>
                                    {c.top_squad && (
                                        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-volt" />
                                    )}
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">{c.county} Co.</div>
                                        {c.squad_count > 0 && (
                                            <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-white/50">{c.squad_count} squad{c.squad_count > 1 ? "s" : ""}</div>
                                        )}
                                    </div>
                                    {c.top_squad ? (
                                        <>
                                            <div className="font-display font-extrabold text-lg leading-tight text-white">{c.top_squad.name}</div>
                                            <div className="font-mono text-[10px] text-volt tracking-[0.24em] mt-0.5">[ {c.top_squad.tag} ]</div>
                                            <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                                                <div className="font-mono text-[10px] text-white/50 uppercase">{c.top_squad.game === "CALL OF DUTY: WARZONE" ? "Warzone" : "MW4 MP"}</div>
                                                <div className="font-display font-black text-lg text-volt">{c.top_squad.elo}</div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-start justify-center min-h-[70px]">
                                            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-white/30">Unclaimed</div>
                                            <div className="mt-2 text-xs text-white/40 leading-snug">Claim this county — first squad plants the flag.</div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {tab !== "counties" && (tab === "players" ? players : teams).length === 0 && tierFilter === "ALL" && (
                        <div className="text-center py-24 border border-white/10 clip-frame-sm">
                            <div className="overline text-white/40 mb-3">No entries yet</div>
                            <div className="font-display text-2xl">
                                {state === "ALL" ? "The national ladder is empty." : `${CODE_TO_NAME[state] || state} ladder opens soon.`}
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <div className="h-32" />
        </div>
    );
}

const StatePill = ({ code, activeCode, onClick, label }) => (
    <button onClick={onClick}
        data-testid={`state-pill-${code}`}
        className={`font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 transition-colors ${activeCode === code ? "bg-volt text-white" : "text-white/50 hover:text-white"}`}>
        {label}
    </button>
);

const TierPill = ({ k, activeKey, setKey, label, color }) => {
    const active = activeKey === k;
    return (
        <button
            onClick={() => setKey(k)}
            data-testid={`tier-pill-${k.toLowerCase()}`}
            className="font-mono text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 transition-all border"
            style={{
                background: active ? color : "transparent",
                color: active ? "#0A0A0A" : color,
                borderColor: color + (active ? "" : "55"),
                boxShadow: active ? `0 0 12px ${color}55` : "none",
                fontWeight: active ? 700 : 400,
            }}
        >
            {label}
        </button>
    );
};
